import { useEffect, useMemo, useState } from "react";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useFinancialYear } from "../context/FinancialYearContext";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Pie } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
);

const n = (v) => Number(v || 0);
const money = (v) =>
  n(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const get = (row, a, b) => row?.[a] ?? row?.[b];

const styles = {
  page: { fontSize: 11, padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "9px 12px",
  },
  title: { fontSize: 13, fontWeight: 800, margin: 0 },
  sub: { fontSize: 10, opacity: 0.9 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3 },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  box: {
    border: "1px solid #e9ecef",
    borderRadius: 12,
    background: "#f8f9fa",
    padding: 10,
  },
  table: { fontSize: 11, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    color: "#fff",
    background: "#07111f",
    whiteSpace: "nowrap",
  },
  td: { fontSize: 11, padding: "6px 8px", whiteSpace: "nowrap" },
};

export default function YearClosingPage() {
  const { financialYear } = useFinancialYear();
  const { user } = useAuth();

  const [fromDate, setFromDate] = useState(financialYear.fromDate);
  const [toDate, setToDate] = useState(financialYear.toDate);
  const [remarks, setRemarks] = useState("");
  const [rows, setRows] = useState([]);
  const [logs, setLogs] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("closing");
  const [chartType, setChartType] = useState("bar");

  const companyId = user?.companyId || null;

  useEffect(() => {
    setFromDate(financialYear.fromDate);
    setToDate(financialYear.toDate);
  }, [financialYear.fromDate, financialYear.toDate]);

  const totalIncome = useMemo(
    () =>
      rows
        .filter((r) => get(r, "AccountType", "accountType") === "Income")
        .reduce((s, r) => s + n(get(r, "Amount", "amount")), 0),
    [rows],
  );

  const totalExpense = useMemo(
    () =>
      rows
        .filter((r) => get(r, "AccountType", "accountType") === "Expense")
        .reduce((s, r) => s + n(get(r, "Amount", "amount")), 0),
    [rows],
  );

  const netProfit = totalIncome - totalExpense;

  const isClosed = rows.some((r) =>
    Boolean(get(r, "IsAlreadyClosed", "isAlreadyClosed")),
  );

  const preview = async () => {
    try {
      setLoading(true);

      const q = new URLSearchParams({ fromDate, toDate });
      if (companyId) q.append("companyId", companyId);

      const previewRes = await client.get(
        `/year-closing/preview?${q.toString()}`,
      );
      setRows(Array.isArray(previewRes.data) ? previewRes.data : []);

      try {
        const healthRes = await client.get(
          `/year-closing/health-check?${q.toString()}`,
        );
        setHealth(healthRes.data || null);
      } catch (healthErr) {
        console.warn("Health check failed:", healthErr);
        setHealth(null);
      }
    } catch (err) {
      console.error("Year closing preview error:", err);
      alert(
        err.response?.data?.message ||
          err.response?.data?.title ||
          "Failed to load year closing preview",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const q = new URLSearchParams();
      if (companyId) q.append("companyId", companyId);

      const res = await client.get(`/year-closing/logs?${q.toString()}`);
      setLogs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn("Year closing logs endpoint not available", err);
    }
  };

  const closeYear = async () => {
    if (!rows.length) return alert("Load preview before closing the year.");
    if (isClosed) return alert("This financial year is already closed.");

    if (health && health.isTrialBalanced === false) {
      return alert("Cannot close year. Trial Balance is not balanced.");
    }

    const ok = window.confirm(
      `Close financial year ${fromDate} to ${toDate}?\nNet ${
        netProfit >= 0 ? "Profit" : "Loss"
      }: ₹ ${money(Math.abs(netProfit))}\n\nThis will create a YEAR CLOSING journal voucher and transfer profit/loss to Retained Earnings.`,
    );

    if (!ok) return;

    try {
      setLoading(true);

      await client.post("/year-closing/close", {
        fromDate,
        toDate,
        companyId,
        closedBy: user?.email || user?.fullName || "SYSTEM",
        remarks,
      });

      alert("Financial year closed successfully.");
      await preview();
      await loadLogs();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to close financial year");
    } finally {
      setLoading(false);
    }
  };

  const carryForward = async () => {
    const nextYearStart = new Date(new Date(toDate).getTime() + 86400000)
      .toISOString()
      .slice(0, 10);

    const ok = window.confirm(
      `Carry forward Balance Sheet balances as opening balances on ${nextYearStart}?`,
    );

    if (!ok) return;

    try {
      setLoading(true);

      const res = await client.post(
        "/year-closing/carry-forward-opening-balances",
        {
          fromDate,
          toDate,
          openingDate: nextYearStart,
          companyId,
          createdBy: user?.email || user?.fullName || "SYSTEM",
          remarks,
        },
      );

      alert(
        res.data?.message || "Opening balances carried forward successfully.",
      );
      await loadLogs();
    } catch (err) {
      console.error(err);
      alert(
        err.response?.data?.message ||
          "Failed to carry forward opening balances",
      );
    } finally {
      setLoading(false);
    }
  };

  const lockYear = async () => {
    const ok = window.confirm(
      `Lock entries from ${fromDate} to ${toDate}?\n\nAfter locking, users cannot add/edit/delete journal entries inside this period.`,
    );

    if (!ok) return;

    try {
      setLoading(true);

      await client.post("/year-closing/lock-year", {
        fromDate,
        toDate,
        companyId,
        lockedBy: user?.email || user?.fullName || "SYSTEM",
        remarks,
      });

      alert("Previous year entries locked successfully.");
      await loadLogs();
    } catch (err) {
      console.error(err);
      alert(
        err.response?.data?.message || "Failed to lock previous year entries",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    preview();
    loadLogs();
    // eslint-disable-next-line
  }, []);

  const chartData = {
    labels: ["Income", "Expense", "Net Result"],
    datasets: [
      {
        label: "Amount",
        data: [totalIncome, totalExpense, Math.abs(netProfit)],
        backgroundColor: [
          "rgba(25,135,84,.78)",
          "rgba(220,53,69,.78)",
          "rgba(13,110,253,.78)",
        ],
        borderColor: [
          "rgba(25,135,84,1)",
          "rgba(220,53,69,1)",
          "rgba(13,110,253,1)",
        ],
        borderWidth: 1.5,
        borderRadius: chartType === "bar" ? 8 : 0,
        hoverOffset: 8,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx) => `₹ ${money(ctx.raw)}` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,.06)" },
        ticks: {
          font: { size: 10 },
          callback: (v) => `₹ ${Number(v).toLocaleString("en-IN")}`,
        },
      },
    },
  };

  const circleOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { font: { size: 11 }, usePointStyle: true },
      },
      tooltip: {
        callbacks: { label: (ctx) => `${ctx.label}: ₹ ${money(ctx.raw)}` },
      },
    },
  };

  const renderChart = () => {
    if (chartType === "pie")
      return <Pie data={chartData} options={circleOptions} />;
    if (chartType === "doughnut")
      return <Doughnut data={chartData} options={circleOptions} />;
    return <Bar data={chartData} options={barOptions} />;
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-calendar-check me-2"></i>
            Financial Year Closing
          </h6>
          <div style={styles.sub}>
            Retained earnings transfer, opening balance carry-forward, and
            previous year entry locking
          </div>
        </div>

        <div style={styles.body}>
          <div className="d-flex gap-1 mb-2 flex-wrap">
            <TabButton
              active={activeTab === "closing"}
              onClick={() => setActiveTab("closing")}
              icon="bi-lock"
              text="Closing"
            />
            <TabButton
              active={activeTab === "carry"}
              onClick={() => setActiveTab("carry")}
              icon="bi-arrow-right-circle"
              text="Carry Forward"
            />
            <TabButton
              active={activeTab === "lock"}
              onClick={() => setActiveTab("lock")}
              icon="bi-shield-lock"
              text="Lock Previous Year"
            />
            <TabButton
              active={activeTab === "logs"}
              onClick={() => {
                setActiveTab("logs");
                loadLogs();
              }}
              icon="bi-clock-history"
              text="Closing Logs"
            />
          </div>

          <div className="row g-2 align-items-end mb-2">
            <Field label="From Date" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Field>

            <Field label="To Date" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Field>

            <Field label="Remarks" className="col-md-4">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional remarks"
              />
            </Field>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-primary btn-sm"
                style={styles.btn}
                onClick={preview}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Preview"}
              </button>
            </div>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-outline-secondary btn-sm"
                style={styles.btn}
                onClick={loadLogs}
                disabled={loading}
              >
                <i className="bi bi-arrow-repeat me-1"></i>
                Refresh Logs
              </button>
            </div>
          </div>

          <HealthCheck health={health} />

          {activeTab === "closing" && (
            <ClosingTab
              rows={rows}
              totalIncome={totalIncome}
              totalExpense={totalExpense}
              netProfit={netProfit}
              isClosed={isClosed}
              closeYear={closeYear}
              loading={loading}
              renderChart={renderChart}
              chartType={chartType}
              setChartType={setChartType}
            />
          )}

          {activeTab === "carry" && (
            <ActionTab
              icon="bi-arrow-right-circle"
              title="Opening Balance Carry Forward"
              text="Creates one opening balance journal voucher for next financial year using Assets, Liabilities and Equity balances from the closed year. Existing vouchers are not edited."
              button="Carry Forward Opening Balances"
              variant="success"
              onClick={carryForward}
              disabled={loading}
            />
          )}

          {activeTab === "lock" && (
            <ActionTab
              icon="bi-shield-lock"
              title="Lock Previous Year Entries"
              text="Locks the selected financial year period. SQL trigger blocks insert/update/delete of journal entries in the locked period to protect audited books."
              button="Lock Previous Year Entries"
              variant="danger"
              onClick={lockYear}
              disabled={loading}
            />
          )}

          {activeTab === "logs" && <LogsTable logs={logs} />}
        </div>
      </div>
    </div>
  );
}

function HealthCheck({ health }) {
  if (!health) return null;

  const checks = [
    ["Trial Balance", health.isTrialBalanced, health.trialMessage],
    ["Closing Status", !health.isAlreadyClosed, health.closingMessage],
    ["Income/Expense", health.hasPLRecords, health.plMessage],
  ];

  return (
    <div className="row g-2 mb-2">
      {checks.map(([title, ok, msg]) => (
        <div className="col-md-4" key={title}>
          <div
            style={{
              ...styles.box,
              borderTop: `3px solid ${ok ? "#0f766e" : "#dc3545"}`,
            }}
          >
            <div className="fw-bold" style={{ fontSize: 11 }}>
              {ok ? (
                <i className="bi bi-check-circle text-success me-1"></i>
              ) : (
                <i className="bi bi-x-circle text-danger me-1"></i>
              )}
              {title}
            </div>
            <div className="text-muted" style={{ fontSize: 10 }}>
              {msg}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ClosingTab({
  rows,
  totalIncome,
  totalExpense,
  netProfit,
  isClosed,
  closeYear,
  loading,
  renderChart,
  chartType,
  setChartType,
}) {
  return (
    <>
      <div className="row g-2 mb-2">
        <Stat title="Income" value={totalIncome} color="#0f766e" />
        <Stat title="Expense" value={totalExpense} color="#dc3545" />
        <div className="col-md-4">
          <div style={styles.box}>
            <div className="fw-bold" style={{ fontSize: 10 }}>
              Net Result
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: netProfit >= 0 ? "#0f766e" : "#dc3545",
              }}
            >
              {netProfit >= 0 ? "Profit" : "Loss"}: ₹{" "}
              {money(Math.abs(netProfit))}
            </div>
            <span
              className={`badge ${isClosed ? "text-bg-secondary" : "text-bg-warning"}`}
            >
              {isClosed ? "Already Closed" : "Open"}
            </span>
          </div>
        </div>
      </div>

      <div className="row g-2 mb-2">
        <div className="col-md-7">
          <div style={styles.box}>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <b>Closing Graph</b>
              <select
                className="form-select form-select-sm"
                style={{ width: 150, fontSize: 11 }}
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
              >
                <option value="bar">Bar Chart</option>
                <option value="pie">Pie Chart</option>
                <option value="doughnut">Wagon Wheel</option>
              </select>
            </div>
            <div style={{ height: 230 }}>{renderChart()}</div>
          </div>
        </div>

        <div className="col-md-5">
          <div className="alert alert-info py-2 mb-0" style={{ fontSize: 11 }}>
            Closing creates one <b>YCLOSE-</b> voucher and transfers profit/loss
            to <b>Retained Earnings</b>.
            <div className="mt-2">
              <button
                className="btn btn-success btn-sm"
                style={styles.btn}
                onClick={closeYear}
                disabled={loading || isClosed || !rows.length}
              >
                <i className="bi bi-lock me-1"></i>
                Close Year
              </button>
            </div>
          </div>
        </div>
      </div>

      <PreviewTable rows={rows} />
    </>
  );
}

function ActionTab({ icon, title, text, button, variant, onClick, disabled }) {
  return (
    <div className="row g-2">
      <div className="col-lg-8">
        <div style={styles.box}>
          <h6 className="fw-bold mb-1" style={{ fontSize: 13 }}>
            <i className={`bi ${icon} me-2`}></i>
            {title}
          </h6>
          <p className="text-muted mb-2" style={{ fontSize: 11 }}>
            {text}
          </p>
          <button
            className={`btn btn-${variant} btn-sm`}
            style={styles.btn}
            onClick={onClick}
            disabled={disabled}
          >
            <i className={`bi ${icon} me-1`}></i>
            {button}
          </button>
        </div>
      </div>
    </div>
  );
}

function LogsTable({ logs }) {
  return (
    <div
      style={{
        maxHeight: 420,
        overflow: "auto",
        border: "1px solid #e9ecef",
        borderRadius: 10,
      }}
    >
      <table
        className="table table-bordered table-hover align-middle"
        style={styles.table}
      >
        <thead>
          <tr>
            <th style={styles.th}>Start</th>
            <th style={styles.th}>End</th>
            <th style={styles.th}>Closing Date</th>
            <th style={styles.th}>Net Profit</th>
            <th style={styles.th}>Locked</th>
            <th style={styles.th}>Opening CF</th>
            <th style={styles.th}>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {!logs.length ? (
            <tr>
              <td
                colSpan="7"
                className="text-center text-muted"
                style={{ ...styles.td, padding: 24 }}
              >
                No year closing logs found
              </td>
            </tr>
          ) : (
            logs.map((r, i) => (
              <tr key={i}>
                <td style={styles.td}>
                  {String(get(r, "StartDate", "startDate") || "").slice(0, 10)}
                </td>
                <td style={styles.td}>
                  {String(get(r, "EndDate", "endDate") || "").slice(0, 10)}
                </td>
                <td style={styles.td}>
                  {String(get(r, "ClosingDate", "closingDate") || "").slice(
                    0,
                    10,
                  )}
                </td>
                <td className="text-end" style={styles.td}>
                  {money(get(r, "NetProfit", "netProfit"))}
                </td>
                <td style={styles.td}>
                  {get(r, "IsLocked", "isLocked") ? (
                    <span className="badge text-bg-danger">Locked</span>
                  ) : (
                    <span className="badge text-bg-warning">Open</span>
                  )}
                </td>
                <td style={styles.td}>
                  {get(
                    r,
                    "OpeningBalanceJournalEntryId",
                    "openingBalanceJournalEntryId",
                  ) ? (
                    <span className="badge text-bg-success">Done</span>
                  ) : (
                    <span className="badge text-bg-secondary">Pending</span>
                  )}
                </td>
                <td style={styles.td}>{get(r, "Remarks", "remarks")}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function PreviewTable({ rows }) {
  return (
    <div
      style={{
        maxHeight: 390,
        overflow: "auto",
        border: "1px solid #e9ecef",
        borderRadius: 10,
      }}
    >
      <table
        className="table table-bordered table-hover align-middle"
        style={styles.table}
      >
        <thead>
          <tr>
            <th style={styles.th}>Code</th>
            <th style={styles.th}>Account</th>
            <th style={styles.th}>Type</th>
            <th className="text-end" style={styles.th}>
              Closing Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {!rows.length ? (
            <tr>
              <td
                colSpan="4"
                className="text-center text-muted"
                style={{ ...styles.td, padding: 24 }}
              >
                No income/expense records found
              </td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr key={i}>
                <td style={styles.td}>
                  {get(r, "AccountCode", "accountCode")}
                </td>
                <td style={styles.td}>
                  {get(r, "AccountName", "accountName")}
                </td>
                <td style={styles.td}>
                  {get(r, "AccountType", "accountType")}
                </td>
                <td className="text-end" style={styles.td}>
                  {money(get(r, "Amount", "amount"))}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function Stat({ title, value, color }) {
  return (
    <div className="col-md-4">
      <div style={{ ...styles.box, borderTop: `3px solid ${color}` }}>
        <div className="fw-bold" style={{ fontSize: 10, color }}>
          {title}
        </div>
        <div style={{ fontSize: 16, fontWeight: 800 }}>₹ {money(value)}</div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon, text }) {
  return (
    <button
      type="button"
      className={`btn btn-sm ${active ? "btn-dark" : "btn-outline-dark"}`}
      style={{ ...styles.btn, height: 28 }}
      onClick={onClick}
    >
      <i className={`bi ${icon} me-1`}></i>
      {text}
    </button>
  );
}
